
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line } from 'recharts';
import { FaUniversity, FaFileInvoiceDollar, FaUsers, FaUser } from 'react-icons/fa';
import axios from '../api/axios';
import logo from './ayto.png';


const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const TarjetaEstadistica = ({ titulo, valor, icono, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <h3 className="text-lg font-medium mb-2">{titulo}</h3>
        <p className="text-3xl font-bold">{valor}</p>
      </div>
      <div className="text-5xl opacity-80">{icono}</div>
    </div>
  </div>
);

const Tarjeta = ({ children, titulo, descripcion }) => (
  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
    <h3 className="text-xl font-semibold mb-2">{titulo}</h3>
    <p className="text-sm text-gray-600 mb-4">{descripcion}</p>
    {children}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalUsuarios: 0,
    totalTerceros: 0,
    totalBancos: 0,
    totalFacturas: 0,
    sepulturasVacias: 0,
    porcentajeOcupacion: 0
  });
  const [datosTerceros, setDatosTerceros] = useState([]);
  const [datosBancos, setDatosBancos] = useState([]);
  const [datosFacturas, setDatosFacturas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [usersResponse, tercerosResponse, bancosResponse, facturasResponse,sepulturasResponse,concesionesResponse] = await Promise.all([
          axios.get('/users'),
          axios.get('/terceros'),
          axios.get('/bancos'),
         // axios.get('/facturas')
          axios.get('/sepulturasc'),
          axios.get('/sepulturasc'),
          axios.get('/concesiones')
        ]);

        const totalSepulturas = sepulturasResponse.data.length;
        const sepulturasVacias = sepulturasResponse.data.filter(sepultura =>
          sepultura.ocupado === 0 || 
          sepultura.ocupado === "0" || 
          sepultura.ocupado === false || 
          sepultura.ocupado == null
        ).length;
        const sepulturasOcupadas = totalSepulturas - sepulturasVacias;
  
        const nivelOcupacion = totalSepulturas > 0 ? ((sepulturasOcupadas / totalSepulturas) * 100).toFixed(2) : 0;

        const currentYear = new Date().getFullYear();
        const concesionesRegistradas = concesionesResponse.data.filter(concesion => {
        const fechaRegistro = new Date(concesion.fecha);
        return fechaRegistro.getFullYear() === currentYear;
      }).length;



      const concesionesPeriodo = concesionesResponse.data.filter(concesion => {
        const fechaRegistro = new Date(concesion.fecha);
        return fechaRegistro.getFullYear() >= currentYear - 1 && fechaRegistro.getFullYear() <= currentYear;
      });

      // Calculamos el promedio de concesiones por año
      const promedioConcesiones = concesionesPeriodo.length / 2;

      // Estimación de concesiones para el próximo año
      const sepulturasAConstruir = Math.ceil(promedioConcesiones);








      
        setStats({
          totalUsuarios: usersResponse.data.length,
          totalTerceros: tercerosResponse.data.length,
          totalBancos: bancosResponse.data.length,
          //totalFacturas: facturasResponse.data.length,
          totalFacturas: sepulturasOcupadas, // Usamos sepulturas ocupadas como totalFacturas
          sepulturasVacias: sepulturasVacias,
          nivelOcupacion: nivelOcupacion,          
          sepulturasVacias: sepulturasResponse.data.filter(sepultura => sepultura.ocupado === 0 || sepultura.ocupado === "0" || sepultura.ocupado === false || sepultura.ocupado == null).length,
          concesionesRegistradas: concesionesRegistradas,
          sepulturasAConstruir: sepulturasAConstruir
        });
      

        

        const tercerosCounts = tercerosResponse.data.reduce((acc, tercero) => {
          acc[tercero.tipoDocumento] = (acc[tercero.tipoDocumento] || 0) + 1;
          return acc;
        }, {});
        setDatosTerceros(Object.entries(tercerosCounts).map(([tipo, cantidad]) => ({ tipo, cantidad })));

        const bancosCounts = bancosResponse.data.reduce((acc, banco) => {
          acc[banco.nombreBanco] = (acc[banco.nombreBanco] || 0) + 1;
          return acc;
        }, {});
        setDatosBancos(Object.entries(bancosCounts).map(([nombre, cantidad]) => ({ nombre, cantidad })));

        const facturasByDate = facturasResponse.data.reduce((acc, factura) => {
          const date = new Date(factura.fechaFactura);
          const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
          acc[formattedDate] = (acc[formattedDate] || 0) + parseFloat(factura.totalConIva);
          return acc;
        }, {});
        const sortedFacturas = Object.entries(facturasByDate)
          .sort(([dateA], [dateB]) => {
            const [dayA, monthA, yearA] = dateA.split('/').map(Number);
            const [dayB, monthB, yearB] = dateB.split('/').map(Number);
            return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
          })
          .map(([fecha, total]) => ({ fecha, total: Number(total.toFixed(2)) }));
        setDatosFacturas(sortedFacturas);

      } catch (err) {
        setError('Error al cargar los datos. Por favor, intente de nuevo.');
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return <div className="text-center mt-8">Cargando...</div>;
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto p-6">
    
      <div className="flex items-center justify-center mb-8">
             <img src={logo} alt="Logo Ayuntamiento" className="h-24 mr-4" />
           <h1 className="text-4xl font-bold text-gray-800 font-serif">Gestión del Cementerio Municipal</h1>
       </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <TarjetaEstadistica
          titulo="Total Usuarios"
          valor={stats.totalUsuarios.toLocaleString()}
          icono={<FaUser />}
          color="from-blue-500 to-blue-700"
        />
        <TarjetaEstadistica
          titulo="Total Terceros"
          valor={stats.totalTerceros.toLocaleString()}
          icono={<FaUsers />}
          color="from-green-500 to-green-700"
        />
        <TarjetaEstadistica
          titulo="Total Bancos"
          valor={stats.totalBancos.toLocaleString()}
          icono={<FaUniversity />}
          color="from-yellow-500 to-orange-600"
        />
        <TarjetaEstadistica
          titulo="Total de sepulturas"
            valor={stats.totalFacturas.toLocaleString()}
         
          icono={<FaUniversity />}
          color="from-red-500 to-purple-600"
        />
       <TarjetaEstadistica
          titulo="Nivel de ocupación"    
          valor={`${stats.nivelOcupacion}%`}   
          icono={<FaUniversity />}
          color="from-red-500 to-orange-600"
       />
       <TarjetaEstadistica
          titulo="Sepulturas vacías"    
          valor={stats.sepulturasVacias.toLocaleString()}  
           
          icono={<FaUniversity />}
          color="from-blue-600 to-indigo-500"
        />
      <TarjetaEstadistica
          titulo="Concesiones registradas durante el año"      
          valor={stats.concesionesRegistradas.toLocaleString()} 
          icono={<FaUniversity />}
          color="from-green-500 to-emerald-600"
      />
      <TarjetaEstadistica
          titulo="Sepulturas a construir"    
          valor={stats.sepulturasAConstruir.toLocaleString()}   
          icono={<FaUniversity />}
          color="from-purple-500 to-pink-600"
      />

      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
        <Tarjeta titulo="Distribución de Terceros" descripcion="Por tipo de documento">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={datosTerceros}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="tipo" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="cantidad">
                {datosTerceros.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Tarjeta>

        <Tarjeta titulo="Distribución de Bancos" descripcion="Por nombre de banco">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={datosBancos}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="cantidad">
                {datosBancos.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Tarjeta>

        <Tarjeta titulo="Vencimiento de concesiones" descripcion="Total concesiones">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={datosFacturas}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fecha" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="total" stroke="#8884d8" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Tarjeta>
      </div>
    </div>
  );
}

/* CORRECTO

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line } from 'recharts';
import { FaUniversity, FaFileInvoiceDollar, FaUsers, FaUser } from 'react-icons/fa';
import axios from '../api/axios';
import logo from './ayto.jpg';


const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const TarjetaEstadistica = ({ titulo, valor, icono, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <h3 className="text-lg font-medium mb-2">{titulo}</h3>
        <p className="text-3xl font-bold">{valor}</p>
      </div>
      <div className="text-5xl opacity-80">{icono}</div>
    </div>
  </div>
);

const Tarjeta = ({ children, titulo, descripcion }) => (
  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
    <h3 className="text-xl font-semibold mb-2">{titulo}</h3>
    <p className="text-sm text-gray-600 mb-4">{descripcion}</p>
    {children}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalUsuarios: 0,
    totalTerceros: 0,
    totalBancos: 0,
    totalFacturas: 0
  });
  const [datosTerceros, setDatosTerceros] = useState([]);
  const [datosBancos, setDatosBancos] = useState([]);
  const [datosFacturas, setDatosFacturas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [usersResponse, tercerosResponse, bancosResponse, facturasResponse] = await Promise.all([
          axios.get('/users'),
          axios.get('/terceros'),
          axios.get('/bancos'),
         // axios.get('/facturas')
          axios.get('/sepulturasc')
        ]);

        setStats({
          totalUsuarios: usersResponse.data.length,
          totalTerceros: tercerosResponse.data.length,
          totalBancos: bancosResponse.data.length,
          totalFacturas: facturasResponse.data.length
        });

        const tercerosCounts = tercerosResponse.data.reduce((acc, tercero) => {
          acc[tercero.tipoDocumento] = (acc[tercero.tipoDocumento] || 0) + 1;
          return acc;
        }, {});
        setDatosTerceros(Object.entries(tercerosCounts).map(([tipo, cantidad]) => ({ tipo, cantidad })));

        const bancosCounts = bancosResponse.data.reduce((acc, banco) => {
          acc[banco.nombreBanco] = (acc[banco.nombreBanco] || 0) + 1;
          return acc;
        }, {});
        setDatosBancos(Object.entries(bancosCounts).map(([nombre, cantidad]) => ({ nombre, cantidad })));

        const facturasByDate = facturasResponse.data.reduce((acc, factura) => {
          const date = new Date(factura.fechaFactura);
          const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
          acc[formattedDate] = (acc[formattedDate] || 0) + parseFloat(factura.totalConIva);
          return acc;
        }, {});
        const sortedFacturas = Object.entries(facturasByDate)
          .sort(([dateA], [dateB]) => {
            const [dayA, monthA, yearA] = dateA.split('/').map(Number);
            const [dayB, monthB, yearB] = dateB.split('/').map(Number);
            return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
          })
          .map(([fecha, total]) => ({ fecha, total: Number(total.toFixed(2)) }));
        setDatosFacturas(sortedFacturas);

      } catch (err) {
        setError('Error al cargar los datos. Por favor, intente de nuevo.');
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return <div className="text-center mt-8">Cargando...</div>;
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto p-6">
    
      <div className="flex items-center justify-center mb-8">
             <img src={logo} alt="Logo Ayuntamiento" className="h-24 mr-4" />
           <h1 className="text-4xl font-bold text-gray-800 font-serif">Gestión del Cementerio Municipal</h1>
       </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <TarjetaEstadistica
          titulo="Total Usuarios"
          valor={stats.totalUsuarios.toLocaleString()}
          icono={<FaUser />}
          color="from-blue-500 to-blue-700"
        />
        <TarjetaEstadistica
          titulo="Total Terceros"
          valor={stats.totalTerceros.toLocaleString()}
          icono={<FaUsers />}
          color="from-green-500 to-green-700"
        />
        <TarjetaEstadistica
          titulo="Total Bancos"
          valor={stats.totalBancos.toLocaleString()}
          icono={<FaUniversity />}
          color="from-yellow-500 to-orange-600"
        />
        <TarjetaEstadistica
          titulo="Sepulturas ocupadas"
            valor={stats.totalFacturas.toLocaleString()}
         
          icono={<FaUniversity />}
          color="from-red-500 to-purple-600"
        />
       <TarjetaEstadistica
          titulo="Nivel de ocupación"       
          icono={<FaUniversity />}
          color="from-red-500 to-orange-600"
       />
       <TarjetaEstadistica
          titulo="Sepulturas vacías"       
          icono={<FaUniversity />}
          color="from-blue-600 to-indigo-500"
        />
      <TarjetaEstadistica
          titulo="Concesiones registradas"       
          icono={<FaUniversity />}
          color="from-green-500 to-emerald-600"
      />
      <TarjetaEstadistica
          titulo="Sepulturas a construir"       
          icono={<FaUniversity />}
          color="from-purple-500 to-pink-600"
      />

      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
        <Tarjeta titulo="Distribución de Terceros" descripcion="Por tipo de documento">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={datosTerceros}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="tipo" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="cantidad">
                {datosTerceros.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Tarjeta>

        <Tarjeta titulo="Distribución de Bancos" descripcion="Por nombre de banco">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={datosBancos}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="cantidad">
                {datosBancos.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Tarjeta>

        <Tarjeta titulo="Vencimiento de concesiones" descripcion="Total concesiones">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={datosFacturas}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fecha" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="total" stroke="#8884d8" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Tarjeta>
      </div>
    </div>
  );
}
*/